import { useParams, useSearchParams } from 'react-router';
import { CalculationType, Priority, Series } from '../types';
import { mergeQueryParams } from '@/utils/merge-query-params';
import { decodeDateFromURL, encodeDateToURL } from '@/utils/dates';
import DateRangePicker from '@/components/date-range-picker/DateRangePicker';
import { DateRange } from 'react-day-picker';
import LineChart, { ChartSeries } from '@/components/charts/line-chart/LineChart';
import { addDays, subDays } from 'date-fns';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { queryLineMeasurementsData, queryRealTimeGenerationData } from '../constants';
import UIError from '@/errors/UIError';
import { enumValueFromString } from '@/utils/enums';
import ErrorAlert from '@/components/error-alert/ErrorAlert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SkeletonCard } from '@/components/skeleton-card/SkeletonCard';
import { Loading } from '@/components/loading/Loading';
import { cn } from '@/lib/utils';

export default function MeasurementGroupGenerationTab() {
	const [searchParams, setSearchParams] = useSearchParams();

	const fromDateParam = searchParams.get('from') ?? '';
	const from = decodeDateFromURL(fromDateParam);

	const toDateParam = searchParams.get('to') ?? '';
	const to = decodeDateFromURL(toDateParam);
	const { measurementGroupId = '' } = useParams();

	const id = parseInt(measurementGroupId, 10);

	const calculationTypeParam = searchParams.get('calculationType') ?? '';
	const calculationType = enumValueFromString(CalculationType, calculationTypeParam, { searchValue: true });

	const priorityTypeParam = searchParams.get('priority') ?? '';
	const priority = enumValueFromString(Priority, priorityTypeParam, { searchValue: true });

	if (isNaN(id)) {
		throw new UIError('Invalid measurement group ID in URL.');
	}

	const {
		error: rtError,
		data: realTimeGenerationSeries,
		isLoading: isRealTimeGenerationLoading,
		isFetching: isRealTimeGenerationFetching
	} = useQuery({
		...queryRealTimeGenerationData({ measurementGroupId: id, from: fromDateParam, to: toDateParam }),
		placeholderData: keepPreviousData
	});

	const {
		error: lineError,
		data: lineMeasurementsSeries,
		isLoading: isLineMeasurementsLoading,
		isFetching: isLineMeasurementFetching
	} = useQuery({
		...queryLineMeasurementsData({
			measurementGroupId: id,
			calculationType: calculationType!,
			priority,
			from: fromDateParam,
			to: toDateParam
		}),
		enabled: Boolean(fromDateParam && toDateParam && calculationType),
		placeholderData: keepPreviousData
	});

	const isApiCallLoading =
		isRealTimeGenerationFetching ||
		isRealTimeGenerationLoading ||
		isLineMeasurementFetching ||
		isLineMeasurementsLoading;

	const onDateRangeSelected = (dateRange: DateRange | undefined) => {
		if ((dateRange?.from && !dateRange?.to) || (dateRange?.to && !dateRange?.from)) {
			return;
		}

		const updated = mergeQueryParams(searchParams, {
			from: encodeDateToURL(dateRange?.from),
			to: encodeDateToURL(dateRange?.to)
		});

		setSearchParams(updated);
	};

	const onDateRangeReset = () => {
		setSearchParams(mergeQueryParams(searchParams, { from: undefined, to: undefined }));
	};

	const onCalculationTypeChange = (value: string): void => {
		const queryParams = mergeQueryParams(searchParams, {
			calculationType: enumValueFromString(CalculationType, value, { searchValue: true })
		});

		setSearchParams(queryParams);
	};

	const onPriorityTypeChange = (value: string): void => {
		const queryParams = mergeQueryParams(searchParams, {
			priority: enumValueFromString(Priority, value, { searchValue: true })
		});

		setSearchParams(queryParams);
	};

	if (rtError) {
		return <ErrorAlert title="Error" description="Error fetching real time generation." />;
	}

	if (lineError) {
		return <ErrorAlert title="Error" description="Error fetching line measurements." />;
	}

	const isDataPresent = realTimeGenerationSeries || lineMeasurementsSeries;

	if (!isDataPresent && isApiCallLoading) {
		return <SkeletonCard />;
	}

	return (
		<>
			<div className="flex gap-5">
				<DateRangePicker
					className="mb-10 w-fit"
					onSelect={onDateRangeSelected}
					onClear={onDateRangeReset}
					defaultValue={{
						from: from ?? subDays(new Date(), 3),
						to: to ?? addDays(new Date(), 1)
					}}
					label="Date Range"
				/>

				<Select
					onValueChange={onCalculationTypeChange}
					defaultValue={calculationType}
					value={calculationType ?? ''}
				>
					<label>
						<span className="text-sm leading-0">Calculated Quantity</span>
						<SelectTrigger className="mt-2 min-w-40">
							<SelectValue placeholder="Select calculation type" />
						</SelectTrigger>
					</label>
					<SelectContent>
						<SelectItem value={CalculationType.Phase}>Phase</SelectItem>
						<SelectItem value={CalculationType.Power}>Power</SelectItem>
						<SelectItem value={CalculationType.ComplexPower}>Complex Power</SelectItem>
						<SelectItem value={CalculationType.ReactivePower}>Reactive Power</SelectItem>
					</SelectContent>
				</Select>

				<Select onValueChange={onPriorityTypeChange} defaultValue={priority} value={priority ?? ''}>
					<label>
						<span className="text-sm leading-0">Priority</span>
						<SelectTrigger className="mt-2 min-w-40">
							<SelectValue placeholder="Select priority" />
						</SelectTrigger>
					</label>

					<SelectContent>
						{Object.entries(Priority).map(([key, value]) => (
							<SelectItem key={key} value={value}>
								{key}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			</div>
			<div>
				<div className={cn({ 'opacity-50 pointer-events-none': isApiCallLoading })}>
					<GenerationChart
						realTimeGenerationSeries={realTimeGenerationSeries?.data}
						lineMeasurementsSeriesList={lineMeasurementsSeries?.data}
					/>
				</div>

				{isApiCallLoading && <Loading className="relative mt-5 w-[500px] lg:w-[1060px] mx-auto" />}
			</div>
		</>
	);
}

function toSeriesData(s: Series): ChartSeries {
	return {
		name: s.name,
		data: s.data.map(pt => ({
			x: new Date(pt.timestamp).getTime(),
			y: parseFloat(pt.value.toFixed(2))
		}))
	};
}

function GenerationChart({
	realTimeGenerationSeries = { name: '', data: [] },
	lineMeasurementsSeriesList = []
}: {
	realTimeGenerationSeries?: Series;
	lineMeasurementsSeriesList?: Series[];
}) {
	const series = [realTimeGenerationSeries, ...lineMeasurementsSeriesList].map(toSeriesData);

	return <LineChart title="" series={series} fillGaps={true} />;
}
